import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tag, Gift as GiftIcon } from "lucide-react";
import { api, cad, cadShort, imgUrl, formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";

const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

export default function Checkout() {
  const { items, giftWrap, clear } = useCart();
  const { storeOpen } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [promo, setPromo] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [giftCard, setGiftCard] = useState("");
  const [appliedGiftCard, setAppliedGiftCard] = useState(null);
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [addr, setAddr] = useState({
    name: "", email: "", line1: "", city: "", province: "ON", postal: "",
  });

  useEffect(() => {
    if (user) setAddr((a) => ({ ...a, name: user.name || "", email: user.email || "" }));
  }, [user]);

  const cartPayload = items.map((i) => ({ product_id: i.product_id, quantity: i.quantity }));

  const refreshQuote = useCallback(async () => {
    if (items.length === 0) return;
    try {
      const { data } = await api.post("/quote", {
        items: cartPayload,
        promo_code: appliedPromo,
        gift_card_code: appliedGiftCard,
        gift_wrap: giftWrap,
      });
      setQuote(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedPromo, appliedGiftCard, giftWrap, items.length]);

  useEffect(() => {
    refreshQuote();
  }, [refreshQuote]);

  const applyPromo = async () => {
    try {
      await api.post("/promo/validate", { code: promo });
      setAppliedPromo(promo.toUpperCase().trim());
      toast.success("Promo code applied");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const applyGiftCard = async () => {
    try {
      const { data } = await api.get(`/giftcard/${giftCard.toUpperCase().trim()}`);
      if (data.balance <= 0) return toast.error("This gift card has no balance");
      setAppliedGiftCard(data.code);
      toast.success(`Gift card applied · ${cadShort(data.balance)} available`);
    } catch (e) {
      toast.error("Gift card not found");
    }
  };

  const placeOrder = async () => {
    if (!storeOpen) {
      return toast.error("We're still stocking our shelves — checkout opens soon.");
    }
    if (!addr.name || !addr.email || !addr.line1 || !addr.city || !addr.postal) {
      return toast.error("Please complete your shipping details");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout", {
        items: cartPayload,
        promo_code: appliedPromo,
        gift_card_code: appliedGiftCard,
        gift_wrap: giftWrap,
        gift_note: "",
        shipping_address: addr,
        origin_url: window.location.origin,
      });
      if (data.free) {
        clear();
        navigate(`/payment/success?order=${data.order_id}`);
        return;
      }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-32 text-center">
          <h1 className="font-serif text-3xl text-charcoal">Your cart is empty</h1>
          <Button onClick={() => navigate("/shop")} className="mt-8 bg-charcoal text-white rounded-none hover:bg-gold font-display">Shop the Boutique</Button>
        </div>
      </Layout>
    );
  }

  const field = (key, label, props = {}) => (
    <div>
      <Label className="text-xs eyebrow text-charcoal/70">{label}</Label>
      <Input
        data-testid={`checkout-${key}`}
        value={addr[key]}
        onChange={(e) => setAddr({ ...addr, [key]: e.target.value })}
        className="mt-1.5 h-11 rounded-none border-charcoal/20 bg-white"
        {...props}
      />
    </div>
  );

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 py-12">
        <h1 className="font-serif text-4xl text-charcoal mb-10">Checkout</h1>
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <section>
              <h2 className="font-serif text-xl text-charcoal mb-5">Shipping Details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {field("name", "Full Name")}
                {field("email", "Email", { type: "email" })}
                <div className="sm:col-span-2">{field("line1", "Street Address")}</div>
                {field("city", "City")}
                <div>
                  <Label className="text-xs eyebrow text-charcoal/70">Province</Label>
                  <select
                    data-testid="checkout-province"
                    value={addr.province}
                    onChange={(e) => setAddr({ ...addr, province: e.target.value })}
                    className="mt-1.5 h-11 w-full rounded-none border border-charcoal/20 bg-white px-3 text-sm"
                  >
                    {PROVINCES.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                {field("postal", "Postal Code")}
              </div>
            </section>

            <section>
              <h2 className="font-serif text-xl text-charcoal mb-5">Discounts &amp; Gift Cards</h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs eyebrow text-charcoal/70 flex items-center gap-1.5"><Tag className="h-3 w-3" /> Promo Code</Label>
                  <div className="mt-1.5 flex gap-2">
                    <Input data-testid="promo-input" value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="WELCOME10" className="h-11 rounded-none border-charcoal/20 bg-white" />
                    <Button data-testid="promo-apply-btn" onClick={applyPromo} variant="outline" className="h-11 rounded-none border-charcoal">Apply</Button>
                  </div>
                  {appliedPromo && <p className="text-xs text-gold mt-1.5">✓ {appliedPromo} applied</p>}
                </div>
                <div>
                  <Label className="text-xs eyebrow text-charcoal/70 flex items-center gap-1.5"><GiftIcon className="h-3 w-3" /> Gift Card</Label>
                  <div className="mt-1.5 flex gap-2">
                    <Input data-testid="giftcard-input" value={giftCard} onChange={(e) => setGiftCard(e.target.value)} placeholder="GC-XXXXXXXXXX" className="h-11 rounded-none border-charcoal/20 bg-white" />
                    <Button data-testid="giftcard-apply-btn" onClick={applyGiftCard} variant="outline" className="h-11 rounded-none border-charcoal">Redeem</Button>
                  </div>
                  {appliedGiftCard && <p className="text-xs text-gold mt-1.5">✓ Gift card {appliedGiftCard} applied</p>}
                </div>
              </div>
            </section>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white border border-soft-border rounded-sm p-6 sticky top-28">
              <h2 className="font-serif text-xl text-charcoal mb-5">Your Order</h2>
              <div className="space-y-3 max-h-52 overflow-y-auto pr-1">
                {items.map((it) => (
                  <div key={it.product_id} className="flex gap-3 items-center">
                    <div className="h-12 w-10 overflow-hidden rounded-sm bg-blush shrink-0">
                      {it.image && <img src={imgUrl(it.image)} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-charcoal truncate">{it.name}</p>
                      <p className="text-xs text-charcoal/50">×{it.quantity}</p>
                    </div>
                    <span className="text-xs text-charcoal">{cadShort(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>
              {quote && (
                <div className="mt-5 pt-5 border-t border-soft-border space-y-2 text-sm">
                  <Row label="Subtotal" value={cad(quote.subtotal)} />
                  {quote.discount > 0 && <Row label={`Discount (${quote.promo?.code})`} value={`−${cadShort(quote.discount)}`} gold />}
                  {quote.gift_wrap_fee > 0 && <Row label="Gift wrapping" value={cadShort(quote.gift_wrap_fee)} />}
                  <Row label="Shipping" value={quote.shipping === 0 ? "Free" : cadShort(quote.shipping)} />
                  {quote.gift_card_applied > 0 && <Row label="Gift card" value={`−${cadShort(quote.gift_card_applied)}`} gold />}
                  <div className="flex justify-between pt-3 border-t border-soft-border">
                    <span className="font-serif text-lg text-charcoal">Total</span>
                    <span className="font-serif text-lg text-charcoal" data-testid="order-total">{cad(quote.total)}</span>
                  </div>
                  <p className="text-[0.7rem] text-charcoal/45 pt-1">Sales tax (GST/HST/PST) is calculated by Stripe at payment.</p>
                </div>
              )}
              <Button
                data-testid="checkout-pay-button"
                onClick={placeOrder}
                disabled={submitting || !storeOpen}
                className="w-full mt-6 h-12 bg-charcoal text-white rounded-none hover:bg-gold font-display tracking-wide disabled:opacity-50 disabled:hover:bg-charcoal"
              >
                {!storeOpen ? "Opening Soon" : submitting ? "Redirecting…" : quote?.total === 0 ? "Place Order" : "Pay with Card"}
              </Button>
              {!storeOpen && <p className="text-[0.75rem] text-center text-gold mt-2" data-testid="checkout-closed-note">🌱 We're still stocking our shelves — checkout opens soon.</p>}
              <p className="text-[0.7rem] text-center text-charcoal/45 mt-3">Secure payment via Stripe · Test card 4242 4242 4242 4242</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const Row = ({ label, value, gold }) => (
  <div className="flex justify-between">
    <span className="text-charcoal/70">{label}</span>
    <span className={gold ? "text-gold font-medium" : "text-charcoal"}>{value}</span>
  </div>
);
